import { makeStyles, tokens, Text } from '@coreai/fluentui-react';
import { CheckmarkFilled } from '@fluentui/react-icons';
import { MOBILE_BREAKPOINT } from './constants';
import { SIGNIN_STEPS } from './data';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  section: {
    paddingBlock: '100px',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingBlock: '64px',
    },
  },
  inner: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalXXL,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      gridTemplateColumns: '1fr',
      gap: '40px',
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  eyebrow: {
    display: 'block',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: ottoTokens.landingAccentTextCool,
    marginBlockEnd: tokens.spacingVerticalM,
  },
  heading: {
    display: 'block',
    fontSize: 'clamp(28px, 4vw, 44px)',
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: tokens.colorNeutralForeground1,
    marginBlockEnd: tokens.spacingVerticalL,
  },
  lead: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
  },
  emphasised: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  mockCard: {
    backgroundColor: ottoTokens.landingCardBackground,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    padding: '28px',
    position: 'relative',
    overflow: 'hidden',
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  check: {
    width: '22px',
    height: '22px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: ottoTokens.statusSucceeded,
    color: tokens.colorNeutralForegroundInverted,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: tokens.fontSizeBase200,
  },
  divider: {
    textAlign: 'center',
    color: ottoTokens.landingDimText,
    fontSize: tokens.fontSizeBase200,
    paddingBlock: tokens.spacingVerticalS,
  },
});

export const LowBarrierSection = () => {
  const styles = useStyles();

  return (
    <section id="product" className={styles.section} aria-labelledby="low-barrier-title">
      <div className={styles.inner}>
        <div>
          <Text className={styles.eyebrow}>Low entry barrier</Text>
          <Text as="h2" id="low-barrier-title" className={styles.heading}>
            Sign in and start building. No setup needed.
          </Text>
          <Text className={styles.lead}>
            <span className={styles.emphasised}>Builders:</span> No need for Azure subscriptions,
            resource groups, or cloud infrastructure.{' '}
            <span className={styles.emphasised}>Admins:</span> Complete a one-time setup so builders
            can just sign in and start building.
          </Text>
        </div>
        <div className={styles.mockCard}>
          <div className={styles.steps} aria-hidden="true">
            {SIGNIN_STEPS.map((label) => (
              <div key={label} className={styles.step}>
                <span className={styles.check}>
                  <CheckmarkFilled />
                </span>
                <span>{label}</span>
              </div>
            ))}
            <div className={styles.divider}>No Azure portal. No infrastructure setup.</div>
          </div>
        </div>
      </div>
    </section>
  );
};
